import { Button } from "@/components/ui/button"
import { DialogFooter } from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Typography } from "@/components/ui/typography"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { Loader2 } from "lucide-react"
import { useState } from "react"
import type {
  SupplierInquiryDetail,
  useWaiveOpenRegulations,
} from "../../../api/supplierInquiriesApi"
import { regulationKeyLabels } from "../../../utils/regulationLabel"

/**
 * The close dialog's waiver (TUR-1732): close as answered, accepting the supplier's
 * answer without what a partial answer left open. The reason is required, it is the
 * record of why and stays on the timeline.
 */
export function InquiryWaiverForm({
  inquiry,
  waive,
  onDone,
  onCancel,
}: {
  inquiry: Pick<SupplierInquiryDetail, "id" | "openRegulations">
  waive: ReturnType<typeof useWaiveOpenRegulations>
  onDone: () => void
  onCancel: () => void
}) {
  const { translate } = useTranslation()
  const [reason, setReason] = useState("")
  const list = regulationKeyLabels(inquiry.openRegulations ?? [], translate)

  return (
    <>
      <Typography as="muted" className="text-[12.5px] text-grey5">
        {translate("MC_INQ_WAIVE_HINT", { list })}
      </Typography>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="inquiry-waiver-reason">
          {translate("MC_INQ_WAIVE_REASON_LABEL")}
        </Label>
        <Textarea
          id="inquiry-waiver-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={translate("MC_INQ_WAIVE_REASON_PLACEHOLDER")}
          maxLength={2000}
          rows={3}
        />
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel} disabled={waive.isPending}>
          {translate("MC_DECL_BTN_CANCEL")}
        </Button>
        <Button
          onClick={() =>
            waive.mutate(
              { id: inquiry.id, reason: reason.trim() },
              { onSuccess: onDone },
            )
          }
          disabled={!reason.trim() || waive.isPending}
        >
          {waive.isPending && <Loader2 size={14} className="animate-spin" />}
          {translate("MC_INQ_WAIVE_SUBMIT")}
        </Button>
      </DialogFooter>
    </>
  )
}
